import ReactDOM, { createPortal } from "react-dom";
import useContentful from "./hooks/useContentful";
import PDPValueProps from "./components/PDPValueProps";
import contentfulComponentLoop from "./lib/contentfulComponentLoop";
import getContentfulLocale from "./lib/getContentfulLocale";

const regionMap = {
  "presonus-us-dev1.myshopify.com": "US",
  "presonus-us-prd.myshopify.com": "US",
  "presonus-emea-dev1.myshopify.com": "EMEA",
  "presonus-emea-prd.myshopify.com": "EMEA",
  "jackson-us-dev1.myshopify.com": "US",
  "jackson-us-prd.myshopify.com": "US",
  "jackson-emea-dev1.myshopify.com": "EMEA",
  "jackson-emea-prd.myshopify.com": "EMEA",
  "fender-us-dev1.myshopify.com": "US",
  "fender-us-prd.myshopify.com": "US",
  "fender-emea-dev1.myshopify.com": "EMEA",
  "fender-emea-prd.myshopify.com": "EMEA",
  "fender-au-dev1.myshopify.com": "AU",
  "fender-au-prd.myshopify.com": "AU",
  "fender-jp-dev1.myshopify.com": "JP",
  "fender-jp-prd.myshopify.com": "JP",
};

const ContentfulPDP = () => {
  const reactTargetEl = document.getElementById('contentful-pdp-react-target');
  let sku = reactTargetEl?.dataset.variantSku;
  const shopPermanentDomain = reactTargetEl?.dataset.shopPermanentDomain;
  // @ts-ignore
  const region = shopPermanentDomain ? regionMap[shopPermanentDomain] : 'US';
  const defaultEntryId = reactTargetEl?.dataset.defaultEntryId || '';
  const isBundle = document.getElementById('bundle-carousel') !== null;

  if (isBundle && !sku) {
    const variantData: { id: string; sku: string }[] = JSON.parse(
      document.getElementById("bundle-variants")!.textContent || "[]"
    );
    sku = variantData.length > 0 ? variantData[0].sku : undefined;
  }

  if (!sku && !isBundle) {
    return;
  }

  let localeString = reactTargetEl?.dataset.shopLocale;
  let localeMarket = reactTargetEl?.dataset.shopMarket;
  const locale = getContentfulLocale(localeString, localeMarket);

  // The Shopify product id is save as one property of a stringified 
  // JSON object on a Contentful short text field.
  const { contentfulData } = useContentful({
    'fields.sku[match]': sku,
    content_type: 'productData',
    include: 10,
    locale
  });

  // Ideally, this wouldn't be fetched if the above returned data.
  const { contentfulData: fallbackContentfulData } = useContentful({
    'sys.id': defaultEntryId,
    include: 10,
    locale
  });
  
  let pdpData;

  if (contentfulData && contentfulData?.items.length > 0) {
    // @ts-ignore First, look for a region specific instance 
    pdpData = contentfulData.items.find(i => i.fields.region.length == 1 && i.fields.region.includes(region));
    // Next, look for an instance that includes this region
    if (!pdpData) {
      // @ts-ignore
      pdpData = contentfulData.items.find(i => i.fields.region.includes(region));
    }

    // Lastly, look for an instance that includes "ALL"
    if (!pdpData) {
      // @ts-ignore
      pdpData = contentfulData.items.find(i => i.fields.region.includes('ALL'));
    }
  }
  
  if (!pdpData && !defaultEntryId) {
    return;
  }

  // If there is no sku specific data, attempt to use the fallback from theme settings.
  if (!pdpData && fallbackContentfulData && fallbackContentfulData.items.length == 1) {
    pdpData = fallbackContentfulData.items[0];
  }

  // If none of the above conditions are met, the data should not be shown for this sku.
  if (!pdpData) {
    return;
  }
  
const { 
  valueProps, 
  contentSlot1, 
  contentSlot2, 
  ctaSlot1, 
  ctaSlot2, 
  contentSlot3
} = pdpData.fields;
const ctaSlot1TargetEl = document.getElementById('contentful-pdp-cta-slot-1');
const ctaSlot2TargetEl = document.getElementById('contentful-pdp-cta-slot-2');
  const contentSlot1TargetEl = document.getElementById('contentful-pdp-slot-1');
  const contentSlot2TargetEl = document.getElementById('contentful-pdp-slot-2');
  const contentSlot3TargetEl = document.getElementById('contentful-pdp-slot-3');
  const valuePropsTargetEl = document.getElementById('contentful-pdp-value-props');

  const applyPadding = (el: HTMLElement | null, hasContent: boolean) => {
    if (el) {
      el.style.paddingTop = hasContent ? "80px" : "0px";
      el.style.paddingBottom = hasContent ? "0px" : "0px";

      if (window.innerWidth >= 750) {
        el.style.paddingTop = hasContent ? "120px" : "0px";
        el.style.paddingBottom = hasContent ? "0px" : "0px";
      }
    }
  };

  if (contentSlot1TargetEl) {
    applyPadding(contentSlot1TargetEl, !!contentSlot1);
  }
  if (contentSlot2TargetEl) {
    applyPadding(contentSlot2TargetEl, !!contentSlot2);
  }
  // value prop padding not included because it's included in liquid template for #MainContent buffer

  return (
  <>
    {ctaSlot1 && ctaSlot1TargetEl && createPortal(
      contentfulComponentLoop(ctaSlot1),
      ctaSlot1TargetEl
    )}
    {ctaSlot2 && ctaSlot2TargetEl && createPortal(
      contentfulComponentLoop(ctaSlot2),
      ctaSlot2TargetEl
    )}
    {contentSlot1 && contentSlot1TargetEl && createPortal(
      contentfulComponentLoop(contentSlot1),
      contentSlot1TargetEl
    )}
    {contentSlot2 && contentSlot2TargetEl && createPortal(
      contentfulComponentLoop(contentSlot2),
      contentSlot2TargetEl
    )}
    {contentSlot3 && contentSlot3TargetEl && createPortal(
      contentfulComponentLoop(contentSlot3),
      contentSlot3TargetEl
    )}
    {valueProps && valuePropsTargetEl && createPortal(
      contentfulComponentLoop(valueProps), 
      valuePropsTargetEl
    )}
  </>
);
}

ReactDOM.render(<ContentfulPDP />, document.getElementById('contentful-pdp-react-target'));